import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { processingTaskKeys } from "../api/queries/queryKeys";

export type ProcessingTaskStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";
export type ProcessingTaskType = "refresh_invoices" | "refresh_emails";

export interface ProcessingServiceTask {
  job_id: string;
  task_type: ProcessingTaskType;
  status: ProcessingTaskStatus;
  payload?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  phase?: string | null;
}

const TERMINAL_STATUSES = new Set<ProcessingTaskStatus>([
  "completed",
  "failed",
]);
const POLL_INTERVAL_MS = 3000;

export function useProcessingServiceTaskPolling(
  jobId: string | null,
  options?: Omit<
    UseQueryOptions<ProcessingServiceTask>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: processingTaskKeys.task(jobId ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<ProcessingServiceTask>(
        `/organization/processing_tasks/${jobId}`,
      );
      return data;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_STATUSES.has(status)) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    ...options,
  });
}

export function useInflightProcessingTask(
  taskType: ProcessingTaskType | null,
  options?: Omit<
    UseQueryOptions<ProcessingServiceTask | null>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: processingTaskKeys.inflight(taskType ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<ProcessingServiceTask | null>(
        `/organization/processing_tasks/inflight`,
        { params: { task_type: taskType } },
      );
      return data ?? null;
    },
    enabled: !!taskType,
    staleTime: 0,
    ...options,
  });
}
